import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import type { RankingRowBase } from '@/types/rankings';
import { Skeleton } from '@/components/dashboard/DashboardCards';

export type RankingWidgetConfig = {
  title: string;
  subtitle?: string;
  /** Semantic empty title — not "No Data" */
  emptyTitle: string;
  emptyDescription: string;
  viewAllTo: string;
  metricLabel: (row: RankingRowBase, index: number) => string;
  primaryName: (row: RankingRowBase) => string;
  secondaryName: (row: RankingRowBase) => string;
  imageOf: (row: RankingRowBase) => string | null;
  /** Entity type for detail modal */
  entityType: 'restaurant' | 'food' | 'customer' | 'deliveryman';
  rowHref: (row: RankingRowBase) => string | null;
  rowSearch?: (row: RankingRowBase) => Record<string, unknown> | undefined;
};

export function rankName(row: RankingRowBase): string {
  const named = row.name?.trim();
  if (named) return named;
  const person = [row.f_name, row.l_name].filter(Boolean).join(' ').trim();
  if (person) return person;
  if (row.store) {
    const s = row.store.name?.trim() || [row.store.f_name, row.store.l_name].filter(Boolean).join(' ').trim();
    if (s) return s;
  }
  return row.id != null ? `#${row.id}` : '—';
}

export function RankingEmptyState({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="rank-empty" role="status">
      <div className="rank-empty-icon" aria-hidden>
        ∅
      </div>
      <p className="rank-empty-title">{title}</p>
      <p className="rank-empty-desc">{description}</p>
    </div>
  );
}

export function RankingSkeletonRows({ count = 4 }: { count?: number }) {
  return (
    <ul className="rank-list" aria-busy="true">
      {Array.from({ length: count }).map((_, i) => (
        <li key={i} className="rank-row">
          <span className="rank-num">·</span>
          <Skeleton height={36} />
          <Skeleton height={18} />
        </li>
      ))}
    </ul>
  );
}

export function RankingCard({
  title,
  subtitle,
  rows,
  loading,
  error,
  onRetry,
  config,
  onOpenDetail,
}: {
  title: string;
  subtitle?: string;
  rows: RankingRowBase[] | undefined;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  config: RankingWidgetConfig;
  onOpenDetail?: (type: RankingWidgetConfig['entityType'], id: number | string) => void;
}) {
  return (
    <section className="rank-card card" aria-label={title}>
      <header className="rank-card-header">
        <div>
          <h2 className="rank-title">{title}</h2>
          {subtitle ? <p className="muted rank-sub">{subtitle}</p> : null}
        </div>
        <Link to={config.viewAllTo as never} className="rank-view-all">
          View all
        </Link>
      </header>

      {loading ? (
        <RankingSkeletonRows />
      ) : error ? (
        <div className="rank-error" role="alert">
          <p>Could not load {title.toLowerCase()}.</p>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onRetry}>
            Retry
          </button>
        </div>
      ) : !rows || rows.length === 0 ? (
        <RankingEmptyState title={config.emptyTitle} description={config.emptyDescription} />
      ) : (
        <ol className="rank-list">
          {rows.map((row, index) => {
            const href = config.rowHref(row);
            const search = config.rowSearch?.(row);
            const name = config.primaryName(row);
            const secondary = config.secondaryName(row);
            const img = config.imageOf(row);
            const metric = config.metricLabel(row, index);
            const modalId = row.id ?? row.store?.id ?? row.store_id ?? null;

            const inner: ReactNode = (
              <>
                <span className={`rank-num n${Math.min(index + 1, 3)}`} aria-hidden>
                  {index + 1}
                </span>
                {img ? (
                  <img className="rank-avatar" src={img} alt="" width={36} height={36} loading="lazy" />
                ) : (
                  <span className="rank-avatar rank-avatar-fallback" aria-hidden>
                    {name.slice(0, 1).toUpperCase()}
                  </span>
                )}
                <span className="rank-meta">
                  <span className="rank-name" title={name}>
                    {name}
                  </span>
                  {secondary ? <span className="rank-secondary">{secondary}</span> : null}
                </span>
                <span className="rank-metric">{metric}</span>
              </>
            );

            return (
              <li key={String(modalId ?? index)}>
                {onOpenDetail && modalId != null ? (
                  <button
                    type="button"
                    className="rank-row rank-row-link rank-row-button"
                    onClick={() => onOpenDetail(config.entityType, modalId)}
                    aria-label={`View ${name} details`}
                  >
                    {inner}
                  </button>
                ) : href ? (
                  <Link to={href as never} search={search as never} className="rank-row rank-row-link">
                    {inner}
                  </Link>
                ) : (
                  <div className="rank-row">{inner}</div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
